import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Paper,
  List,
  Box,
  Chip,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
} from "@mui/material";
import { Notifications, Clear, MarkEmailRead } from "@mui/icons-material";
import { useNotifications } from "../contexts/NotificationContext";
import { NotificationItem } from "../components/NotificationItem";
import { useNavigate } from "react-router-dom";

const NotificationsPage: React.FC = () => {
  const {
    notifications,
    unreadCount,
    isLoading: loading,
    refreshNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotifications();

  const navigate = useNavigate();
  const [filterType, setFilterType] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState<string>("");

  useEffect(() => {
    refreshNotifications();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMarkAsRead = async (id: string) => {
    await markAsRead(id);
  };

  const handleDelete = async (id: string) => {
    await deleteNotification(id);
  };

  const handleOrderClick = (orderId: string) => {
    navigate(`/orders/${orderId}`);
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const filteredNotifications = notifications.filter((notification) => {
    const matchesType =
      filterType === "all" || notification.type === filterType;
    const matchesSearch =
      searchTerm === "" ||
      notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notification.message.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesType && matchesSearch;
  });

  const getNotificationTypeCount = (type: string) => {
    return notifications.filter((n) => n.type === type).length;
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Notifications color="primary" />
            <Typography variant="h5" component="h1">
              Bildirimler
            </Typography>
            {unreadCount > 0 && (
              <Chip
                label={`${unreadCount} okunmamış`}
                color="primary"
                size="small"
              />
            )}
          </Box>

          {unreadCount > 0 && (
            <Button
              variant="outlined"
              startIcon={<MarkEmailRead />}
              onClick={handleMarkAllAsRead}
            >
              Tümünü Okundu İşaretle
            </Button>
          )}
        </Box>

        {/* Filters */}
        <Box display="flex" gap={2} mb={3} flexWrap="wrap">
          <TextField
            label="Bildirim Ara"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 200 }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Tip</InputLabel>
            <Select
              value={filterType}
              label="Tip"
              onChange={(e) => setFilterType(e.target.value)}
            >
              <MenuItem value="all">Tümü ({notifications.length})</MenuItem>
              <MenuItem value="order_created">
                Yeni Sipariş ({getNotificationTypeCount("order_created")})
              </MenuItem>
              <MenuItem value="order_approved">
                Onaylanan ({getNotificationTypeCount("order_approved")})
              </MenuItem>
              <MenuItem value="order_rejected">
                Reddedilen ({getNotificationTypeCount("order_rejected")})
              </MenuItem>
              <MenuItem value="order_assigned">
                Atanan ({getNotificationTypeCount("order_assigned")})
              </MenuItem>
              <MenuItem value="order_in_transit">
                Yolda ({getNotificationTypeCount("order_in_transit")})
              </MenuItem>
              <MenuItem value="order_delivered">
                Teslim Edilen ({getNotificationTypeCount("order_delivered")})
              </MenuItem>
              <MenuItem value="order_cancelled">
                İptal Edilen ({getNotificationTypeCount("order_cancelled")})
              </MenuItem>
              <MenuItem value="system">
                Sistem ({getNotificationTypeCount("system")})
              </MenuItem>
            </Select>
          </FormControl>

          {(searchTerm || filterType !== "all") && (
            <Button
              variant="outlined"
              startIcon={<Clear />}
              onClick={() => {
                setSearchTerm("");
                setFilterType("all");
              }}
            >
              Filtreleri Temizle
            </Button>
          )}
        </Box>

        {/* Notification Stats */}
        <Box display="flex" gap={1} mb={2} flexWrap="wrap">
          <Chip
            label={`Toplam: ${notifications.length}`}
            variant="outlined"
            size="small"
          />
          <Chip
            label={`Okunmamış: ${unreadCount}`}
            color="primary"
            size="small"
          />
          <Chip
            label={`Filtrelenmiş: ${filteredNotifications.length}`}
            variant="outlined"
            size="small"
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Notifications List */}
        {filteredNotifications.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="200px"
            textAlign="center"
          >
            <Notifications
              sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Bildirim Bulunamadı
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm || filterType !== "all"
                ? "Filtrelerinizi değiştirmeyi deneyin"
                : "Henüz bildiriminiz yok"}
            </Typography>
          </Box>
        ) : (
          <List>
            {filteredNotifications.map((notification, index) => (
              <React.Fragment key={notification._id || notification.id}>
                <NotificationItem
                  notification={notification}
                  onMarkAsRead={handleMarkAsRead}
                  onDelete={handleDelete}
                  onOrderClick={handleOrderClick}
                />
                {index < filteredNotifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
    </Container>
  );
};

export default NotificationsPage;
